import { useState } from "react";
import { useSelector } from "react-redux";

import {
  ITEM_CALCULATED_FIELDS,
  ITEM_CALCULATED_FIELDS_NO_ON_HAND,
} from "appConfig";

import { Decimal } from "classes/DecimalClasses";

import { QuickAddItem } from "components/QuickAddItem";
import { LineTableCell } from "components/formFields/LineTableCell";
import { LineClass } from "components/formFields/line/LineClass";
import { LineDescription } from "components/formFields/line/LineDescription";
import { LineInventoryItemBin } from "components/formFields/line/LineInventoryItemBin";
import { LineJobWorkcenter } from "components/formFields/line/LineJobWorkcenter";
import { LineLinkedTransaction } from "components/formFields/line/LineLinkedTransaction";
import { LineLotWithExpiration } from "components/formFields/line/LineLotWithExpiration";
import { LineOnHand } from "components/formFields/line/LineOnHand";
import { LineOnShortOrder } from "components/formFields/line/LineOnShortOrder";
import { LineQuantity } from "components/formFields/line/LineQuantity";
import { LineSerialsEnter } from "components/formFields/line/LineSerialsEnter";
import { LineUom } from "components/formFields/line/LineUom";

import { i18n } from "services/i18nService";
import { afterTouchOutputLine as afterTouchLine } from "services/sosInventoryService/build/afterTouchOutputLine";
import {
  createLineFromItem,
  postItemAndCreateLineItem,
  getItemRecord,
  clearItemDataFromLine,
} from "services/sosInventoryService/domainLogic";
import { getEmptyRecord as getEmptyItemRecord } from "services/sosInventoryService/item/schema";
import { updateLineWithItem } from "services/sosInventoryService/productionTransaction/domainLogic";
import { setPageDirty } from "services/utility/edit";
import { isLineFieldInError } from "services/utility/errors";
import { isFinishedGoods } from "services/utility/jobWorkCenter";
import { TYPES } from "services/utility/schema";

import { usePlans } from "hooks/usePlans";

import { OBJECT_TYPES } from "appConstants";
import { FINISHED_GOODS } from "appConstants";
import {
  OUTPUT_METADATA,
  OUTPUT_LINE_ITEM_FIELDS,
  EMPTY_OUTPUT_LINE_ITEM,
} from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.BUILD.fullString;

// created this wrapper to make drag and drop code easier to see
export function LineItem(props) {
  return <LineItemDetails {...props} />;
}

function LineItemDetails(props) {
  const {
    objectType,
    record,
    line,
    classes,
    lineHandler,
    items,
    bins,
    relatedRecords,
    jobWorkcenters,
    itemsCount,
    addItem,
    lineItemsErrors: errors,
    setErrors,
  } = props;
  const { date, location, workcenter, autoSerialLotNumbering } = record;

  const [addNewItem, setAddNewItem] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [lineLoading, setLineLoading] = useState(false);
  const { hasAtLeastPlusPlan } = usePlans();

  // SETTINGS
  const defaultAssetAccount = useSelector(
    (state) => state.userCompanySettings.settings.defaultAssetAccount,
    (prev, curr) => curr?.id === prev?.id
  );
  const defaultCOGSAccount = useSelector(
    (state) => state.userCompanySettings.settings.defaultCOGSAccount,
    (prev, curr) => curr?.id === prev?.id
  );
  const defaultExpenseAccount = useSelector(
    (state) => state.userCompanySettings.settings.defaultExpenseAccount,
    (prev, curr) => curr?.id === prev?.id
  );
  const defaultIncomeAccount = useSelector(
    (state) => state.userCompanySettings.settings.defaultIncomeAccount,
    (prev, curr) => curr?.id === prev?.id
  );
  const defaultTaxCode = useSelector(
    (state) => state.userCompanySettings.settings.defaultTaxCode,
    (prev, curr) => curr?.id === prev?.id
  );
  const showQuantityOnBuildsAndShipments = useSelector(
    (state) =>
      state.userCompanySettings.settings.showQuantityOnBuildsAndShipments
  );

  function isInError(field) {
    return isLineFieldInError(field, line.lineNumber, errors);
  }

  function handleValueChange(fieldName, newValue) {
    let newLine;
    const lineItemFields = OUTPUT_LINE_ITEM_FIELDS[objectType];
    if (lineItemFields[TYPES.decimal].includes(fieldName)) {
      newLine = { ...line, [fieldName]: new Decimal(newValue || 0) };
    } else {
      newLine = { ...line, [fieldName]: newValue };
    }
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, fieldName, record),
    });
  }

  function handleUomChange(_, uom) {
    setPageDirty();
    const oldUom = line.uom;
    const updatedLine = afterTouchLine({ ...line, uom, oldUom }, "uom");
    lineHandler({ type: "update", updatedLine });
  }

  async function handleItemChange(newItem, userInput) {
    if (!newItem) {
      const updatedLine = clearItemDataFromLine(line);
      setPageDirty();
      lineHandler({ type: "update", updatedLine });
      return;
    }
    if (newItem?.id === "add") {
      return openAddItem(userInput);
    }
    setLineLoading(true);
    const itemFields = showQuantityOnBuildsAndShipments
      ? ITEM_CALCULATED_FIELDS[objectType]
      : ITEM_CALCULATED_FIELDS_NO_ON_HAND[objectType];
    const item = await getItemRecord(
      newItem.id,
      location?.id,
      date,
      itemFields
    );
    const newLine = await updateLineWithItem(item, line, record);
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, "item"),
    });
    setLineLoading(false);
  }

  async function onQuickAddItem(name) {
    const newItemSettings = {
      defaultCOGSAccount,
      defaultAssetAccount,
      defaultExpenseAccount,
      defaultIncomeAccount,
      defaultTaxCode,
    };
    const item = { ...getEmptyItemRecord(newItemSettings), name };
    const updatedLine = await postItemAndCreateLineItem(
      item,
      EMPTY_OUTPUT_LINE_ITEM[OBJECT_TYPE],
      line.lineNumber,
      addItem,
      setErrors
    );
    if (updatedLine) {
      setPageDirty();
      lineHandler({ type: "update", updatedLine });
    }
    setAddNewItem(false);
  }

  function onAddItem(item) {
    setAddNewItem(false);
    const updatedLine = createLineFromItem(
      item,
      EMPTY_OUTPUT_LINE_ITEM[OBJECT_TYPE],
      line.lineNumber,
      lineHandler
    );
    setPageDirty();
    lineHandler({ type: "update", updatedLine });
    addItem();
  }

  function openAddItem(userInput) {
    // handle "Add new item" in Item select input field; this does not change the
    // line item, so no need to proceed further into the handler
    setNewItemName(userInput);
    setAddNewItem(true);
  }

  if (!line) {
    return null;
  }

  return (
    <>
      <LineInventoryItemBin
        lineLoading={lineLoading}
        bins={bins}
        location={relatedRecords.location}
        showBin={record.workcenter?.id === FINISHED_GOODS.id}
        handleItemChange={handleItemChange}
        handleReferenceChange={handleValueChange}
        objectType={OBJECT_TYPE}
        line={line}
        items={items}
        itemsCount={itemsCount}
        addItem={addItem}
        isInError={isInError}
      />
      <LineTableCell sx={{ minWidth: "24rem" }}>
        <LineDescription
          value={line.description}
          onValueChange={handleValueChange}
          error={isInError("description")}
        />
        {isFinishedGoods(workcenter) &&
          !autoSerialLotNumbering &&
          hasAtLeastPlusPlan && (
            <>
              {line.itemDetails?.serialTracking && (
                <LineSerialsEnter
                  value={line.serials}
                  onValueChange={handleValueChange}
                  quantity={line.quantity}
                  error={isInError("serials")}
                />
              )}
              {line.itemDetails?.lotTracking && (
                <LineLotWithExpiration
                  lot={line.lot}
                  lotExpiration={line.lotExpiration}
                  onValueChange={handleValueChange}
                  itemLotTracked={line.itemDetails?.lotTracking}
                  autoSerialLotNumbering={autoSerialLotNumbering}
                  quantity={line.quantity}
                  metadata={OUTPUT_METADATA[OBJECT_TYPE]}
                  lotError={isInError("lot")}
                  lotExpirationError={isInError("lotExpiration")}
                />
              )}
            </>
          )}
      </LineTableCell>
      {showQuantityOnBuildsAndShipments && <LineOnHand value={line.onhand} />}
      <LineOnShortOrder value={line.onSO} />
      <LineQuantity
        objectType={OBJECT_TYPE}
        value={line.quantity}
        onValueChange={handleValueChange}
        error={isInError("quantity")}
        disabled={lineLoading}
      />
      <LineUom
        value={line.uom}
        onValueChange={handleUomChange}
        itemUoms={line.itemDetails?.itemUoms}
        error={isInError("uom")}
      />
      <LineLinkedTransaction
        objectType={OBJECT_TYPE}
        transactionLabel={i18n("workOrder.labelAbbreviatedNumber")}
        value={line.linkedTransaction}
        onValueChange={handleValueChange}
        error={isInError("linkedTransaction")}
      />

      <LineClass
        value={line.class}
        classes={classes}
        onValueChange={handleValueChange}
      />
      <LineJobWorkcenter
        job={line.job}
        workcenter={line.workcenter}
        value={line.jobWorkcenter}
        jobWorkcenters={jobWorkcenters}
        onValueChange={handleValueChange}
      />
      {addNewItem && (
        <QuickAddItem
          open={addNewItem}
          initialNameValue={newItemName}
          onClose={() => setAddNewItem(false)}
          onAdd={onAddItem}
          onQuickAdd={onQuickAddItem}
        />
      )}
    </>
  );
}
